/**
 * useMapController
 * ================
 * Place at: frontend/src/hooks/useMapController.js
 *
 * WHY THIS EXISTS
 * ---------------
 * CosmicMap has always been the single file that DRAWS every map. What was
 * duplicated is the layer above it — the controller.
 *
 * Dashboard.jsx feeds CosmicMap 21 props assembled from seven hooks
 * (useMapViewport, useSelection, useLineage, useScrubber, useFilters,
 * useOverlays, useLens). When the cohort map was added it hand-rolled a
 * subset of 12, and the eight it omitted were exactly the ones that make a
 * map feel alive: onConnectionClick, onChallengeClick, highlightedIds. The
 * result looked like the main map and behaved like a poster of one.
 *
 * Left alone, every future map — campus, public profile, showcase — grows
 * its own half-built stub in the same way. So the behaviour lives here
 * once, and pages spread the result:
 *
 *     const map = useMapController({ projects, connections, challenges });
 *     <CosmicMap {...map.mapProps} />
 *
 * WHAT IT COVERS
 * --------------
 *   - viewport (zoom / pan / reset)
 *   - selection + lineage highlighting on node click
 *   - connection click -> full lineage chain lit + scrubber chain built
 *   - challenge click -> parent and responses lit
 *   - background click -> clear everything
 *
 * WHAT IT DELIBERATELY DOES NOT COVER
 * -----------------------------------
 * Overlays, curated branches, the lens, multi-select forking, and search.
 * Those are page-specific orchestration and belong to the page. This hook
 * owns interaction with the MAP, not what the page does afterwards. Pages
 * hook in through the onOpenProject / onOpenChallenge callbacks.
 *
 * MIGRATION STATUS
 * ----------------
 * BOTH maps now use this hook. That is the point — while two
 * implementations existed they drifted, and every map bug found on
 * 2026-09-05 came from that drift (wrong connection shape, missing
 * highlight clear on multi-select entry).
 *
 * Dashboard injects its own viewport/selection/lineage/scrubber because
 * its scrubber lives in TrayContext and is shared with SelectionTray.
 * CohortDashboard injects nothing and gets private instances.
 *
 * A behaviour change here now affects both maps. That is the desired
 * property, and it is also the risk: test both before shipping one.
 */

import { useState, useCallback, useMemo } from 'react';
import useMapViewport from './useMapViewport';
import useSelection from './useSelection';
import useLineage from './useLineage';
import useScrubber from './useScrubber';

export default function useMapController({
  projects = [],
  connections = [],
  challenges = [],
  isLoading = false,
  onOpenProject,
  onOpenChallenge,
  activeZones = [],
  showChallenges = true,

  // ── Dependency injection ────────────────────────────────────────────
  // Pages that already own this state pass it in; pages that do not get
  // a private instance.
  //
  // This exists because Dashboard takes `scrubber` from TrayContext, not
  // from a local useScrubber(). If the controller always made its own,
  // Dashboard would end up with TWO scrubbers — the controller writing to
  // one while SelectionTray reads scrubberLineage from the other. The
  // Journey panel would just quietly stop updating, with no error.
  //
  // Injection also means the migration moves BEHAVIOUR into this hook
  // without moving STATE OWNERSHIP, which is the low-risk order to do it.
  viewport: injectedViewport,
  selection: injectedSelection,
  lineage: injectedLineage,
  scrubber: injectedScrubber,

  // Fired whenever a project becomes the selection. Dashboard uses it for
  // recent.recordRecentProject; the cohort map passes nothing.
  onProjectSelected,

  // Dashboard opens the scrubber on a plain node click. The cohort map
  // does not. A real behavioural difference, so it is a flag rather than
  // something one page silently inherits from the other.
  openScrubberOnSelect = false,

  // Return true to take over a node click completely. Dashboard uses this
  // for branch editing, where shift-click edits the active collection
  // instead of building a fork selection.
  interceptProjectClick,

  // Passed through to CosmicMap untouched.
  extraMapProps = null,
} = {}) {
  // Hooks must run unconditionally, so a private instance is always
  // created. When an injected one is supplied the private copy is simply
  // unused — cheap, and it keeps the rules of hooks intact.
  const ownViewport = useMapViewport();
  const ownSelection = useSelection(projects);
  const ownLineage = useLineage(projects);
  const ownScrubber = useScrubber();

  const viewport = injectedViewport || ownViewport;
  const selection = injectedSelection || ownSelection;
  const lineage = injectedLineage || ownLineage;
  const scrubber = injectedScrubber || ownScrubber;

  // Which two projects a clicked connection joins. Drives the story card.
  const [connectionInfo, setConnectionInfo] = useState(null);

  // ── Node click: light the whole lineage, dim everything else ──────────
  // Shift-click (or a tap while already in multi-select mode) adds to the
  // selection instead. Multi-select exists to feed multi-parent forking:
  // pick three projects, fork them into one. That is the platform thesis
  // in a single gesture, and a classroom is where it is most likely to
  // happen, so it belongs in the shared controller rather than only on
  // the main map.
  const handleProjectClick = useCallback((project, event) => {
    if (!project?.id) return;

    // Page-specific override first — e.g. Dashboard's branch editing.
    if (interceptProjectClick?.(project, event)) return;

    if (event?.shiftKey || selection.isMultiSelectMode) {
      if (!selection.isMultiSelectMode) {
        selection.enterMultiSelect();
        scrubber.closeScrubber();
        setConnectionInfo(null);

        // Drop the lineage highlight on the way in. enterMultiSelect()
        // only flips the mode — it leaves highlightedIds alone. If a node
        // was clicked normally first, its lineage stays highlighted and
        // everything outside it keeps dimming, INCLUDING nodes the user
        // then shift-clicks. The green ring draws correctly but the node
        // underneath is still faded, so the selection looks broken.
        //
        // In multi-select, the selection IS the emphasis. Nothing should
        // be dimmed by a stale lineage.
        selection.setHighlightedIds([]);
        selection.setSelectedProject(null);
        selection.setSelectedChallenge(null);
      }
      selection.toggleMultiSelected(project.id);
      return;
    }

    // Clicking the selected node again clears it. Without this a selection
    // can only be undone by finding empty space, which on a dense map is
    // genuinely hard.
    if (selection.selectedProject?.id === project.id) {
      selection.clearAll();
      setConnectionInfo(null);
      scrubber.closeScrubber();
      return;
    }

    selection.setSelectedProject(project);
    selection.setSelectedChallenge(null);
    onProjectSelected?.(project);

    const chain = lineage.computeLineage(project);
    selection.setHighlightedIds(chain);

    if (openScrubberOnSelect && chain.length >= 1) {
      scrubber.openScrubber(lineage.buildLineageChain(project));
    } else if (openScrubberOnSelect) {
      scrubber.closeScrubber();
    }

    setConnectionInfo(null);
  }, [selection, lineage, scrubber, interceptProjectClick,
      onProjectSelected, openScrubberOnSelect]);

  const handleProjectDoubleClick = useCallback((project) => {
    onOpenProject?.(project);
  }, [onOpenProject]);

  // ── Connection click: light both lineages, open the scrubber ──────────
  // Mirrors Dashboard.handleConnectionClick. The union of both endpoints'
  // lineages is used, not just one, so plucking a line mid-chain lights the
  // entire story rather than half of it.
  const handleConnectionClick = useCallback((connection) => {
    if (!connection) return;
    const { sourceId, targetId, sourceType, targetType } = connection;

    // Challenge edges are handled by the challenge click path.
    if (sourceType !== 'project' || targetType !== 'project') return;

    const source = projects.find(p => p.id === sourceId);
    const target = projects.find(p => p.id === targetId);
    if (!source || !target) return;

    const combined = [...new Set([
      ...lineage.computeLineage(source),
      ...lineage.computeLineage(target),
    ])];

    selection.setHighlightedIds(combined);
    selection.setSelectedProject(target);
    selection.setSelectedChallenge(null);
    onProjectSelected?.(target);
    scrubber.openScrubber(lineage.buildLineageChain(target));
    setConnectionInfo({ source, target });
  }, [projects, lineage, selection, scrubber, onProjectSelected]);

  // ── Challenge click: light the parent project and every response ──────
  const handleChallengeClick = useCallback((challenge) => {
    if (!challenge) return;
    selection.setSelectedChallenge(challenge);
    selection.setSelectedProject(null);

    const parentId = challenge.project_id || challenge.projectId;
    const responseIds =
      challenge.responseProjectIds || challenge.response_project_ids || [];

    selection.setHighlightedIds([
      `c-${challenge.id}`,
      ...(parentId ? [parentId] : []),
      ...(Array.isArray(responseIds) ? responseIds : []),
    ]);
    setConnectionInfo(null);
  }, [selection]);

  const handleChallengeDoubleClick = useCallback((challenge) => {
    onOpenChallenge?.(challenge);
  }, [onOpenChallenge]);

  // ── Background click: clear everything ────────────────────────────────
  const handleBackgroundClick = useCallback(() => {
    // In multi-select, a background click means "I'm done picking", not
    // "throw away what I picked". Dropping the selection here would make
    // the mode unusable — one stray click on empty space and three
    // deliberate picks are gone.
    if (selection.isMultiSelectMode) {
      selection.exitMultiSelect();
      return;
    }
    selection.clearAll();
    setConnectionInfo(null);
    scrubber.closeScrubber();
  }, [selection, scrubber]);

  // ── The complete prop bundle ──────────────────────────────────────────
  // Spread straight onto <CosmicMap>. Keeping this in one memo is the
  // point: a page cannot accidentally omit a handler, because it never
  // sees the individual props.
  const mapProps = useMemo(() => ({
    projects,
    connections,
    challenges,
    showChallenges,
    activeZones,
    isLoading,

    selectedProjectId: selection.selectedProject?.id ?? null,
    selectedChallengeId: selection.selectedChallenge?.id ?? null,
    highlightedIds: selection.highlightedIds,
    multiSelectedIds: selection.multiSelectedIds,
    isMultiSelectMode: selection.isMultiSelectMode,

    zoom: viewport.zoom,
    pan: viewport.pan,
    onZoomChange: viewport.setZoom,
    onPanChange: viewport.setPan,

    onProjectClick: handleProjectClick,
    onProjectDoubleClick: handleProjectDoubleClick,
    onChallengeClick: handleChallengeClick,
    onChallengeDoubleClick: handleChallengeDoubleClick,
    onConnectionClick: handleConnectionClick,
    onBackgroundClick: handleBackgroundClick,

    ...(extraMapProps || {}),
  }), [
    projects, connections, challenges, showChallenges, activeZones, isLoading,
    selection.selectedProject, selection.selectedChallenge,
    selection.highlightedIds, selection.multiSelectedIds,
    selection.isMultiSelectMode,
    viewport.zoom, viewport.pan, viewport.setZoom, viewport.setPan,
    handleProjectClick, handleProjectDoubleClick,
    handleChallengeClick, handleChallengeDoubleClick,
    handleConnectionClick, handleBackgroundClick, extraMapProps,
  ]);

  return {
    mapProps,
    viewport,
    selection,
    lineage,
    scrubber,
    connectionInfo,
    clearConnectionInfo: () => setConnectionInfo(null),
  };
}